import { useCallback, useEffect, useRef, useState } from "react";
import { getActivePresentationBytesShared } from "../services/pptxBytesShared";
import { compareDecks, type ComparisonReport } from "../services/runComparison";

export interface UseCompareSlides {
  busy: boolean;
  error: string | null;
  /** Full slide-by-slide comparison report (null until a comparison runs). */
  report: ComparisonReport | null;
  /** Diff an uploaded original deck against the live presentation. */
  compare: (origBuffer: ArrayBuffer) => Promise<void>;
  /** Clear the current error/report (e.g. when a new original is picked). */
  reset: () => void;
}

/**
 * Orchestrates the Compare Slides flow: reads the live deck bytes, runs the
 * (worker-backed) diff, and exposes the full comparison report so the panel can
 * drive its slide-by-slide review UI. Cancels any in-flight comparison on
 * unmount or when a new run starts.
 */
export function useCompareSlides(): UseCompareSlides {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ComparisonReport | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Cancel any in-flight comparison if the consumer unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    setError(null);
    setReport(null);
  }, []);

  const compare = useCallback(async (origBuffer: ArrayBuffer) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const current = await getActivePresentationBytesShared(true);
      const result = await compareDecks(origBuffer, current, { signal: controller.signal });
      setReport(result);
    } catch (err) {
      // Swallow deliberate cancellations (unmount / re-run).
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }, []);

  return { busy, error, report, compare, reset };
}
